import { Theme, Themes } from "../settings/Themes.js";

/**
 * Class to implement the toggle-theme functionality
 * as a singleton, which can be instantiated by
 * any other extension as a dependency
 */
export class ToggleTheme {
    private static instance: ToggleTheme;

    public static readonly selector = `scope="toggle-theme"`;

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        Themes.use();
        this.buttons.forEach((button) => this.handle(button));
    }

    /**
     * Method to instantiate all
     * buttons on the current page
     *
     * @returns {ToggleTheme}
     */
    public static use(): ToggleTheme {
        if (!ToggleTheme.instance) {
            ToggleTheme.instance = new ToggleTheme();
        }
        return ToggleTheme.instance;
    }

    /**
     * Selects the buttons
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    protected get buttons(): NodeListOf<HTMLElement> {
        return document.querySelectorAll(`[${ToggleTheme.selector}]`);
    }

    /**
     * Handles the action on the button
     *
     * @param {HTMLElement} button
     * @returns {void}
     */
    protected handle(button: HTMLElement): void {
        // Set the aria attribute depending on the state
        button.setAttribute(
            "aria-checked",
            window.themes.value === Theme.Dark ? "true" : "false",
        );

        // Handle toggle requests
        button.addEventListener("click", () => {
            switch (window.themes.value) {
                // If the current mode is "dark", switch to "light"
                case Theme.Dark:
                    window.themes.value = Theme.Light;
                    break;

                // If the current mode is "light", switch to "dark"
                case Theme.Light:
                    window.themes.value = Theme.Dark;
                    break;

                // Switch to the dark-mode if no state is defined
                default:
                    window.themes.value = Theme.Dark;
            }

            // Set the aria attribute depending on the state
            button.setAttribute(
                "aria-checked",
                window.themes.value === Theme.Dark ? "true" : "false",
            );
        });
    }
}
